/**
 * LayoutAudioGate — viewer-level audio policy for every template.
 *
 * The video tiles all render with `<video muted>` so the browser autoplay
 * policy is happy. That means none of them ship audio on their own.
 * Audio plays through the shared event LiveKit audio renderer (mounted by
 * LayoutEventViewer inside `<LiveKitRoom>`), which renders subscribed remote audio
 * track. This gate's only job is to keep the watching camera's publication
 * subscribed — plus any `extraIdentities`, such as the watch screen's live
 * stage faces — so the renderer plays those and nothing else.
 *
 *   - only the watching camera's (and the extra identities') audio
 *     publications are `setSubscribed(true)`; every other camera is
 *     unsubscribed (avoids the "audio from 4 cams at once" failure mode and
 *     saves bandwidth).
 *   - we deliberately do NOT also render a manual <AudioTrack> for the
 *     active track — RoomAudioRenderer would play it once and the manual
 *     element would play it again, causing duplicated/louder audio. The
 *     subscription model alone is enough.
 *
 * This component is mounted by `LayoutEventViewer` directly — NOT as a
 * primitive. That's deliberate: forking a layout in the Director must
 * never break audio. The gate runs regardless of what the layout JSON
 * contains.
 */

import { useEffect } from 'react'
import { RemoteTrackPublication, Track } from 'livekit-client'
import { useEventLiveKitAudioTrackRefs } from '@/components/livekit/EventLiveKitProvider'

export function LayoutAudioGate({
  activeIdentity,
  extraIdentities,
}: {
  activeIdentity: string | null
  /** Also audible, alongside the watched camera (the watch screen's live faces). */
  extraIdentities?: readonly string[]
}) {
  const audioTrackRefs = useEventLiveKitAudioTrackRefs()

  // Subscribe only to the watched camera's audio and the extra identities';
  // unsubscribe everyone else. The viewer-level audio renderer plays the
  // result after browser unlock.
  useEffect(() => {
    audioTrackRefs.forEach((trackRef) => {
      if (trackRef.participant.isLocal || trackRef.publication.kind !== Track.Kind.Audio) return
      const identity = trackRef.participant.identity
      const shouldSubscribe = Boolean(
        (activeIdentity && identity === activeIdentity) || extraIdentities?.includes(identity),
      )
      if (trackRef.publication instanceof RemoteTrackPublication) {
        if (trackRef.publication.isDesired !== shouldSubscribe) {
          trackRef.publication.setSubscribed(shouldSubscribe)
        }
      }
    })
  }, [audioTrackRefs, activeIdentity, extraIdentities])

  return null
}
